import React from 'react';
import { Label } from '@/components/atoms/texts/label';
import PrintErrorMessage from '@/components/organisms/feedback/PrintErrorMessage';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/organisms/forms/select';

const DEFAULT_OPERATION_TYPES = [
    { value: 'INITIALIZATION', labelKey: 'consultants.statement.statementOperations.statementOperationType_INITIALIZATION' },
    { value: 'OTHER', labelKey: 'consultants.statement.statementOperations.statementOperationType_OTHER' },
];

export default function StatementOperationForm({
    formData,
    setFormData,
    currencies,
    loadingCurrencies,
    errorCurrencies,
    t,
    operationTypeOptions = DEFAULT_OPERATION_TYPES,
    showOperationType = true,
    submissionError,
}) {
    const mergedOperationTypeOptions = React.useMemo(() => {
        const options = [...operationTypeOptions];
        const currentOperationType = formData?.operationType;

        if (currentOperationType && !options.some((option) => option.value === currentOperationType)) {
            options.unshift({
                value: currentOperationType,
                labelKey: `consultants.statement.statementOperations.statementOperationType_${currentOperationType}`,
                label: currentOperationType,
            });
        }

        return options;
    }, [operationTypeOptions, formData?.operationType]);

    return (
        <div className="flex flex-col gap-secondDegree p-thirdDegree">
            <div className="flex items-center gap-secondDegree">
                <Label fontWeight="bold" message="consultants.expense.includeOverflow?" />
                <Switch
                    checked={!!formData.impactsOverflow}
                    onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, impactsOverflow: checked }))}
                />
            </div>

            <div className="grid grid-cols-4 items-center gap-secondDegree w-full">
                <Label className="col-span-1" fontWeight="bold" message="consultants.expense.date" />
                <Input
                    className="col-span-3"
                    type="date"
                    value={formData.valueDate || ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, valueDate: e.target.value }))}
                />
            </div>

            {showOperationType && (
                <div className="grid grid-cols-4 items-center gap-secondDegree w-full">
                    <Label className="col-span-1" fontWeight="bold">Type</Label>
                    <Select
                        value={formData.operationType}
                        onValueChange={(value) => setFormData((prev) => ({ ...prev, operationType: value }))}
                    >
                        <SelectTrigger className="col-span-3 p-2 border rounded-md text-body bg-white border-greyStrong">
                            <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                            {mergedOperationTypeOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.labelKey ? t(option.labelKey) : option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}

            <div className="grid grid-cols-4 items-center gap-secondDegree w-full">
                <Label className="col-span-1" fontWeight="bold">Montant</Label>
                <Input
                    className="col-span-3"
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData((prev) => ({ ...prev, amount: e.target.value }))}
                />
            </div>

            <div className="grid grid-cols-4 items-center gap-secondDegree w-full">
                <Label className="col-span-1" fontWeight="bold" message="consultants.expense.currency" />
                {errorCurrencies ? <PrintErrorMessage error={errorCurrencies} className={'col-span-3'} /> : (
                    <Select
                        value={formData.currency}
                        onValueChange={(value) => setFormData((prev) => ({ ...prev, currency: value }))}
                    >
                        <SelectTrigger className="col-span-3 p-2 border rounded-md text-body bg-white border-greyStrong">
                            <SelectValue placeholder="Currency" />
                        </SelectTrigger>
                        <SelectContent>
                            {loadingCurrencies ? <span className="loader" /> : currencies?.map((currency, index) => (
                                <SelectItem key={currency.code ?? index} value={currency.code}>
                                    <Label>{currency.code} {currency.symbol}</Label>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>

            <div className="grid grid-cols-4 items-center gap-secondDegree w-full">
                <Label className="col-span-1" fontWeight="bold" message="consultants.expense.comment" />
                <Input
                    className="col-span-3"
                    value={formData.comment || ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, comment: e.target.value }))}
                />
            </div>

            {submissionError && <PrintErrorMessage error={submissionError} />}
        </div>
    );
}
